import React from 'react';
import PricingArchitectureDerivative from '@app/model/pricing-automation/PricingArchitectureDerivative';
import useModal from '@app/component/overlays/modal/ModalRef';
import Modal from '@app/component/overlays/modal/Modal';
import useAppForm from '@app/site/_form';
import PricingArchitectureDerivativeRuleType from '@app/model/pricing-automation/PricingArchitectureDerivativeRuleType';
import * as z from 'zod/v4';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import PricingAutomationService from '@app/model/pricing-automation/service/PricingAutomationService';
import { PricingAutomationQueryKeys } from '@app/model/pricing-automation/service/PricingAutomationQueryKeys';


export interface EditPricingArchitectureDerivativeRuleModalProps {
	companyHotelId: number;
	architectureId: number;
	derivative: PricingArchitectureDerivative;
	onClose: () => void;
}

const EditPricingArchitectureDerivativeRuleModal = React.memo(
	function EditPricingArchitectureDerivativeRuleModal({
																												architectureId,
																												companyHotelId,
																												derivative,
																												onClose,
																											}: EditPricingArchitectureDerivativeRuleModalProps) {

		const modal = useModal();
		const queryClient = useQueryClient();
		const toaster = useToaster();

		const editArchitectureDerivativeMutation = useMutation({
			mutationFn: PricingAutomationService.editPricingArchitectureDerivative,
			onSuccess: () => {
				Promise.all([
					queryClient.invalidateQueries({
						queryKey: PricingAutomationQueryKeys.pricingArchitectures(companyHotelId),
					}),
					queryClient.invalidateQueries({
						queryKey: PricingAutomationQueryKeys.notAssignedPricingRooms(companyHotelId),
					}),
				]).then(() => {
					modal.close();
					onClose();
					toaster.success('Pricing Architecture', 'Pricing Architecture Derivative saved successfully');
				});
			},
		});

		const derivativeSchema = z.object({
			architectureId: z.number().min(1, 'Please select an architecture'),
			companyHotelId: z.number().min(1, 'Please select company hotel'),
			derivativeId: z.number().min(1, 'Please select a derivative'),
			ruleType: z.nativeEnum(PricingArchitectureDerivativeRuleType),
			ruleValue: z.string(),
		}).superRefine((value, ctx) => {
			if (value.ruleType !== PricingArchitectureDerivativeRuleType.SAME) {
				const ruleValueNumber = value.ruleValue === undefined || value.ruleValue.trim() === ''
					? Number.NaN
					: Number(value.ruleValue);

				if (Number.isNaN(ruleValueNumber)) {
					ctx.addIssue({
						code: 'custom',
						path: ['ruleValue'],
						message: 'Please enter a valid value',
					});
				}
			}
		});

		const form = useAppForm({
			defaultValues: {
				architectureId: architectureId,
				companyHotelId: companyHotelId,
				derivativeId: derivative.id,
				ruleType: derivative.rule.type,
				ruleValue: derivative.rule.value?.toString() ?? '',
			},
			validators: {
				onChange: derivativeSchema,
			},
			onSubmit: ({ value }) =>
				editArchitectureDerivativeMutation.mutateAsync({
					architectureId: value.architectureId,
					companyHotelId: value.companyHotelId,
					derivativeId: value.derivativeId,
					ruleType: value.ruleType,
					ruleValue: !!value.ruleValue ? Number(value.ruleValue) : undefined,
				}).catch(() => toaster.error('Pricing Architecture', 'Pricing Architecture Derivative save failed')),
		});

		return <form.AppForm>
			<form onSubmit={(e) => {
				e.preventDefault();
				e.stopPropagation();
			}}>
				<Modal ref={modal.ref} title="Configure Room" onClose={onClose} show={true} actions={
					<form.SubmitButton state="primary">
						Save
					</form.SubmitButton>}>
					<div className="flex gap-2">
						<div className="flex-1">
							<div className="flex justify-between">
								<label htmlFor="type" className="label">Type</label>
							</div>
							<div className="mt-0.5">
								<form.AppField name="ruleType">
									{field => <field.SelectField extraClassName="w-full"
									                             options={[
																								 {
																									 id: 'SAME',
																									 label: 'Equal',
																									 value: PricingArchitectureDerivativeRuleType.SAME,
																								 },
																								 {
																									 id: 'PERCENTAGE',
																									 label: 'Percentage (%)',
																									 value: PricingArchitectureDerivativeRuleType.PERCENTAGE,
																								 },
																								 {
																									 id: 'AMOUNT',
																									 label: 'Amount',
																									 value: PricingArchitectureDerivativeRuleType.AMOUNT,
																								 },
																							 ]} />}
								</form.AppField>
							</div>
						</div>
						<div className="flex-1">
							<div className="flex justify-between">
								<label htmlFor="difference" className="label">Differential</label>
							</div>
							<div className="mt-0.5">
								<form.Subscribe selector={(state) => state.values.ruleType}>
									{(ruleType) => (
										<form.AppField name="ruleValue">
											{(field) => (
												<field.TextField
													extraClassName="w-full"
													disabled={ruleType === PricingArchitectureDerivativeRuleType.SAME}
												/>
											)}
										</form.AppField>
									)}
								</form.Subscribe>
							</div>
						</div>
					</div>
				</Modal>
			</form>
		</form.AppForm>;
	});

export default EditPricingArchitectureDerivativeRuleModal;